import React, {
  useRef,
  useEffect,
  useMemo,
  useContext,
  useCallback,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'redux/hooks';
import { locationSelectors } from 'redux/modules/Location';
import { COUNT_CHAR_FOR_START_SEARCH } from 'config';
import { useCurrentUser, useDebounce, useMediaQuery } from 'hooks';
import { captureEvent } from 'utils/analytics/captureEvent';
import { AnalyticsEvents } from 'enums';
import { searchDiscount } from 'redux/modules/Discounts/discounts';
import { search } from 'static/icons/resizable';
import { SideBarContentType } from 'interfaces';
import { IconPositionInInput } from 'view/atoms';
import { SearchList } from '../SearchList';
import { HeaderContext } from '../HeaderContext';
import { useOutSideClick } from './hooks';
import * as S from './Search.styles';

export const Search = () => {
  const user = useCurrentUser();

  const appLocation = useLocation();

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const currentLocation = useAppSelector(locationSelectors.getCurrentLocation);

  const { mobile, tablet } = useMediaQuery();

  const isSideBarMode = mobile || tablet;

  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const [searchInputKey, setSearchInputKey] = useState('inputSearch');

  const {
    isSearchListShown,
    isSideBarOpen,
    sideBarContentType,
    showSearchList,
    hideSearchList,
    openSideBar,
    closeSideBar,
    setSideBarType,
    setSearchedStatus,
  } = useContext(HeaderContext);

  /* 
     We don't render whole component,
     when we are in sideBarMode (mobile mode)
     and menu with categories is shown.
  */
  const isNullRender =
    isSideBarMode &&
    sideBarContentType === SideBarContentType.menu &&
    isSideBarOpen;

  /* 
     We don't show search line,
     when we are on mobile and SearchList is hidden
     (sideBar is hidden too).
  */
  const showOnlyIconSearch = !isSideBarOpen && mobile;

  const [searchValue, changeHandler, resetSearch] = useDebounce(300);

  const focusOnSearchInput = () => {
    searchInputRef.current && searchInputRef.current.focus();
  };

  const openSearchListWithSideBar = () => {
    setSideBarType(SideBarContentType.search);

    showSearchList();

    !isSideBarOpen && openSideBar();
  };

  const closeSearchListWithSideBar = () => {
    closeSideBar();
    hideSearchList();
    setSideBarType(SideBarContentType.menu);
  };

  const handleFocusOnSearchInput = () => {
    if (user?.id) {
      captureEvent(AnalyticsEvents.SearchFocus, {
        user_id: String(user.id),
        event_time: new Date().toISOString(),
      });
    }

    if (isSideBarMode) {
      openSearchListWithSideBar();
      return;
    }

    showSearchList();
  };

  const handleClickOnSearchIcon = () => {
    if (!showOnlyIconSearch) return;

    isSideBarMode && openSearchListWithSideBar();
  };

  const sendSearchRequest = useCallback((word: string, id: number) => {
    word.length >= COUNT_CHAR_FOR_START_SEARCH &&
      id &&
      dispatch(searchDiscount(word.trim(), id));
  }, []);

  useEffect(() => {
    currentLocation && sendSearchRequest(searchValue, currentLocation.id);

    setSearchedStatus(searchValue.length > 0);
  }, [searchValue, currentLocation]);

  useEffect(() => {
    if (searchValue && !isSideBarMode) {
      resetSearch();
      setSearchInputKey(`inputSearch${Math.random()}`);
    }
  }, [appLocation, isSideBarOpen]);

  useEffect(() => {
    isSideBarOpen && closeSideBar();
    isSearchListShown && hideSearchList();
  }, [appLocation, isSideBarMode]);

  useEffect(() => {
    isSideBarOpen && isSearchListShown && focusOnSearchInput();
  }, [isSideBarOpen]);

  useOutSideClick(
    'safe-to-click',
    closeSearchListWithSideBar,
    isSearchListShown,
  );

  const SearchIcon = useMemo(
    () => (
      <S.IconSearch
        src={search}
        alt='Search'
        className='safe-to-click'
        onClick={handleClickOnSearchIcon}
        isSingle={showOnlyIconSearch}
      />
    ),
    [showOnlyIconSearch],
  );

  if (isNullRender) return null;

  return (
    <S.SearchBox className='safe-to-click'>
      {showOnlyIconSearch ? (
        SearchIcon
      ) : (
        <S.SearchInput
          data-testid='Header-SearchInput'
          type='text'
          defaultValue={searchValue}
          onFocus={handleFocusOnSearchInput}
          onChange={changeHandler}
          placeholder={t('search.searchTitle')}
          active={isSearchListShown}
          ref={searchInputRef}
          icon={SearchIcon}
          key={searchInputKey}
          iconPosition={IconPositionInInput.right}
          className='safe-to-click'
        />
      )}

      {isSearchListShown && (
        <S.AnswerModal>
          <S.AnswerContent>
            <SearchList />
          </S.AnswerContent>
        </S.AnswerModal>
      )}
    </S.SearchBox>
  );
};
